/**
 * Palette Scoring — consolidated from both SPA and shared-logic implementations.
 *
 * Provides two scoring interfaces:
 * - scorePalette(): Simple API/MCP-facing function (harmony/accessibility/comprehensive)
 * - calculatePaletteFitness(): Rich use-case-based scoring with grades, recommendations
 *
 * Uses OKLCH circular hue math for harmony pattern detection.
 * Delegates accessibility math to accessibilityTools.
 */
import Color from 'colorjs.io';
import chroma from 'chroma-js';
import { calculateContrast } from './accessibilityTools.js';
// ── OKLCH helpers ───────────────────────────────────────────────────────────
function circularDistance(a, b) {
    const diff = Math.abs(a - b) % 360;
    return diff > 180 ? 360 - diff : diff;
}
function toOklch(hex) {
    try {
        const color = new Color(hex).to('oklch');
        return {
            l: color.coords[0] || 0,
            c: color.coords[1] || 0,
            h: color.coords[2] || 0,
        };
    }
    catch {
        return { l: 0, c: 0, h: 0 };
    }
}
// ── Harmony pattern detection ───────────────────────────────────────────────
function checkNWayPattern(hues, n, tolerance = 15) {
    const idealGap = 360 / n;
    const sorted = [...hues].sort((a, b) => a - b);
    let bestError = Infinity;
    for (let offset = 0; offset < 360; offset += 5) {
        const ideal = Array.from({ length: n }, (_, i) => (offset + i * idealGap) % 360);
        let totalError = 0;
        for (const targetHue of ideal) {
            const closest = sorted.reduce((best, h) => Math.min(best, circularDistance(h, targetHue)), Infinity);
            totalError += closest;
        }
        const avgError = totalError / n;
        if (avgError < bestError)
            bestError = avgError;
    }
    return { matched: bestError <= tolerance, avgError: bestError };
}
function scoreHarmonyInternal(colors) {
    const oklchs = colors.map(toOklch);
    const chromatic = oklchs.filter((c) => c.c > 0.02);
    if (chromatic.length < 2) {
        const monoScore = chromatic.length === 0 ? 85 : 90;
        return { score: monoScore, pattern: 'monochromatic', details: ['Low chroma palette'] };
    }
    const hues = chromatic.map((c) => c.h);
    const maxHueDist = Math.max(...hues.flatMap((h1, i) => hues.slice(i + 1).map((h2) => circularDistance(h1, h2))));
    // Monochromatic: all hues within 15°
    if (maxHueDist <= 15) {
        return { score: 95, pattern: 'monochromatic', details: [`Hue range: ${maxHueDist.toFixed(1)}°`] };
    }
    // Analogous: all hues within 60°
    if (maxHueDist <= 60) {
        return {
            score: 90,
            pattern: 'analogous',
            details: [`Hue spread: ${maxHueDist.toFixed(1)}° (within 60° analogous range)`],
        };
    }
    // Pattern detection
    const patterns = [
        { name: 'complementary', n: 2, tolerance: 20, baseScore: 95 },
        { name: 'triadic', n: 3, tolerance: 20, baseScore: 94 },
        { name: 'tetradic', n: 4, tolerance: 20, baseScore: 92 },
    ];
    for (const p of patterns) {
        if (chromatic.length >= p.n) {
            const { matched, avgError } = checkNWayPattern(hues, p.n, p.tolerance);
            if (matched) {
                const errorPenalty = Math.max(0, avgError - 5) * 0.5;
                return {
                    score: Math.max(70, Math.round(p.baseScore - errorPenalty)),
                    pattern: p.name,
                    details: [`${p.name} pattern detected (avg error: ${avgError.toFixed(1)}°)`],
                };
            }
        }
    }
    // Split-complementary check
    if (chromatic.length >= 3) {
        for (let i = 0; i < hues.length; i++) {
            const target1 = (hues[i] + 150) % 360;
            const target2 = (hues[i] + 210) % 360;
            const others = hues.filter((_, idx) => idx !== i);
            const close1 = others.some((h) => circularDistance(h, target1) < 20);
            const close2 = others.some((h) => circularDistance(h, target2) < 20);
            if (close1 && close2) {
                return { score: 92, pattern: 'split-complementary', details: ['Split-complementary pattern detected'] };
            }
        }
    }
    // Fallback: wide scatter
    const spacingScore = Math.min(100, 50 + maxHueDist / 4);
    return {
        score: Math.round(spacingScore),
        pattern: 'scatter',
        details: [`Max hue distance: ${maxHueDist.toFixed(1)}°`],
    };
}
// ── Use-case specific metrics (from SPA) ────────────────────────────────────
function calculateContrastScore(colors, targetBackground, targetCompliance) {
    if (colors.length === 0) {
        return {
            score: 0, weight: 0, label: 'Contrast Fitness',
            description: 'No colors to evaluate', details: [], recommendations: ['Add colors to the palette'],
        };
    }
    let passCount = 0;
    const results = [];
    for (const color of colors) {
        const result = calculateContrast(color, targetBackground, { textSizePx: 16, isBold: false });
        if (!result) {
            results.push({ color, ratio: 0, passes: false });
            continue;
        }
        let passes = false;
        if (targetCompliance === 'aa')
            passes = result.wcag.aa.normalText;
        else if (targetCompliance === 'aaa')
            passes = result.wcag.aaa.normalText;
        else
            passes = result.wcag.twoPointTwo.smallText;
        results.push({ color, ratio: result.ratio, passes });
        if (passes)
            passCount++;
    }
    const passRate = (passCount / colors.length) * 100;
    const details = [
        `${passCount}/${colors.length} colors meet ${targetCompliance.toUpperCase()} contrast`,
        `Pass rate: ${passRate.toFixed(1)}%`,
    ];
    const recommendations = [];
    const failedColors = results.filter((r) => !r.passes);
    if (failedColors.length > 0) {
        recommendations.push(`Adjust lightness of ${failedColors.length} color(s) to meet contrast requirements`);
    }
    if (passRate < 50) {
        recommendations.push('Use more extreme lightness values (very light or very dark)');
    }
    return { score: passRate, weight: 0.35, label: 'Contrast Fitness', description: `Measures ${targetCompliance.toUpperCase()} contrast against target background`, details, recommendations };
}
function calculateChromaSpacingScore(colors) {
    if (colors.length < 2) {
        return { score: 100, weight: 0.25, label: 'Chroma Spacing', description: 'Insufficient colors', details: [], recommendations: [] };
    }
    const chromas = colors.map((hex) => {
        try {
            return new Color(hex).to('oklch').coords[1] || 0;
        }
        catch {
            return 0;
        }
    });
    const avgChroma = chromas.reduce((s, c) => s + c, 0) / chromas.length;
    const variance = chromas.reduce((s, c) => s + Math.pow(c - avgChroma, 2), 0) / chromas.length;
    const stdDev = Math.sqrt(variance);
    const idealStdDev = 0.1;
    const score = Math.max(0, 100 - Math.abs(stdDev - idealStdDev) * 500);
    const details = [
        `Avg chroma: ${avgChroma.toFixed(3)}`,
        `Std dev: ${stdDev.toFixed(3)}`,
        `Range: ${Math.min(...chromas).toFixed(3)} - ${Math.max(...chromas).toFixed(3)}`,
    ];
    const recommendations = [];
    if (stdDev < 0.05)
        recommendations.push('Add more saturation variety');
    else if (stdDev > 0.2)
        recommendations.push('Moderate saturation differences');
    return { score, weight: 0.25, label: 'Chroma Spacing', description: 'Saturation distribution', details, recommendations };
}
function calculateHueDiversityScore(colors) {
    if (colors.length < 2) {
        return { score: 100, weight: 0.2, label: 'Hue Diversity', description: 'Insufficient colors', details: [], recommendations: [] };
    }
    const oklchColors = colors.map((hex) => {
        try {
            const c = new Color(hex).to('oklch');
            return { c: c.coords[1] || 0, h: c.coords[2] || 0 };
        }
        catch {
            return { c: 0, h: 0 };
        }
    });
    const chromatic = oklchColors.filter((c) => c.c > 0.02);
    if (chromatic.length < 2) {
        return { score: 50, weight: 0.2, label: 'Hue Diversity', description: 'Mostly achromatic', details: ['Low chroma colors'], recommendations: ['Add chromatic colors'] };
    }
    const hues = chromatic.map((c) => c.h).sort((a, b) => a - b);
    const diffs = [];
    for (let i = 0; i < hues.length; i++) {
        const next = (i + 1) % hues.length;
        let diff = hues[next] - hues[i];
        if (i === hues.length - 1)
            diff = 360 - hues[i] + hues[0];
        diffs.push(Math.abs(diff));
    }
    const minDiff = Math.min(...diffs);
    const span = Math.max(...hues) - Math.min(...hues);
    const minScore = Math.min(100, (minDiff / 30) * 100);
    const spanScore = Math.min(100, (span / 180) * 100);
    const score = minScore * 0.6 + spanScore * 0.4;
    const details = [`${chromatic.length}/${colors.length} chromatic`, `Min spacing: ${minDiff.toFixed(1)}°`, `Span: ${span.toFixed(1)}°`];
    const recommendations = [];
    if (minDiff < 30)
        recommendations.push('Increase hue separation');
    if (span < 90)
        recommendations.push('Add complementary colors');
    return { score, weight: 0.2, label: 'Hue Diversity', description: 'Hue distribution', details, recommendations };
}
// Use-case-specific metric functions
function darkUIMetric(colors) {
    const lightCount = colors.filter((hex) => { try {
        return new Color(hex).to('oklch').coords[0] > 0.7;
    }
    catch {
        return false;
    } }).length;
    const score = lightCount >= 2 ? 100 : (lightCount / 2) * 100;
    return { score, weight: 0.2, label: 'Dark UI Suitability', description: 'Lightness for dark mode', details: [`Light colors: ${lightCount}`], recommendations: lightCount < 2 ? ['Add light colors for text on dark backgrounds'] : [] };
}
function lightUIMetric(colors) {
    const darkCount = colors.filter((hex) => { try {
        return new Color(hex).to('oklch').coords[0] < 0.4;
    }
    catch {
        return false;
    } }).length;
    const score = darkCount >= 2 ? 100 : (darkCount / 2) * 100;
    return { score, weight: 0.2, label: 'Light UI Suitability', description: 'Lightness for light mode', details: [`Dark colors: ${darkCount}`], recommendations: darkCount < 2 ? ['Add dark colors for text on light backgrounds'] : [] };
}
function dataVizMetric(colors) {
    let minDeltaE = Infinity;
    for (let i = 0; i < colors.length; i++) {
        for (let j = i + 1; j < colors.length; j++) {
            try {
                minDeltaE = Math.min(minDeltaE, new Color(colors[i]).deltaE2000(new Color(colors[j])));
            }
            catch { /* skip */ }
        }
    }
    const score = Math.min(100, (minDeltaE / 10) * 100);
    return { score, weight: 0.2, label: 'Data Viz Fitness', description: 'Color distinctiveness', details: [`Min ΔE: ${minDeltaE.toFixed(2)}`], recommendations: minDeltaE < 5 ? ['Increase color distinctiveness'] : [] };
}
function brandMetric(colors) {
    const score = colors.length >= 3 && colors.length <= 7 ? 100 : 70;
    return { score, weight: 0.2, label: 'Brand Fitness', description: 'Palette size', details: [`${colors.length} colors`], recommendations: colors.length < 3 ? ['Add more colors (3-7 ideal)'] : colors.length > 7 ? ['Simplify to 3-7 colors'] : [] };
}
function printMetric(colors) {
    const safe = colors.filter((hex) => { try {
        return (new Color(hex).to('oklch').coords[1] || 0) < 0.25;
    }
    catch {
        return false;
    } }).length;
    const score = (safe / colors.length) * 100;
    return { score, weight: 0.2, label: 'Print Fitness', description: 'CMYK gamut safety', details: [`${safe}/${colors.length} print-safe`], recommendations: score < 80 ? ['Reduce saturation for CMYK safety'] : [] };
}
function webMetric(colors) {
    const light = colors.filter((h) => { try {
        return new Color(h).to('oklch').coords[0] > 0.65;
    }
    catch {
        return false;
    } }).length;
    const dark = colors.filter((h) => { try {
        return new Color(h).to('oklch').coords[0] < 0.45;
    }
    catch {
        return false;
    } }).length;
    const score = light >= 1 && dark >= 1 ? 100 : 50;
    return { score, weight: 0.2, label: 'Web Accessibility', description: 'Pairing flexibility', details: [`Light: ${light}, Dark: ${dark}`], recommendations: score < 100 ? ['Add both light and dark colors'] : [] };
}
const USE_CASE_CONFIGS = {
    'dark-ui-aa': { label: 'Dark UI (WCAG AA)', description: 'Dark mode with AA contrast', targetBackground: '#1a1a1a', targetCompliance: 'aa', weights: { contrast: 0.35, chromaSpacing: 0.25, hueDiversity: 0.2, useCaseSpecific: 0.2 }, specificMetric: darkUIMetric },
    'light-ui-aa': { label: 'Light UI (WCAG AA)', description: 'Light mode with AA contrast', targetBackground: '#ffffff', targetCompliance: 'aa', weights: { contrast: 0.35, chromaSpacing: 0.25, hueDiversity: 0.2, useCaseSpecific: 0.2 }, specificMetric: lightUIMetric },
    'dark-ui-aaa': { label: 'Dark UI (WCAG AAA)', description: 'Dark mode with AAA contrast', targetBackground: '#1a1a1a', targetCompliance: 'aaa', weights: { contrast: 0.4, chromaSpacing: 0.2, hueDiversity: 0.2, useCaseSpecific: 0.2 }, specificMetric: darkUIMetric },
    'light-ui-aaa': { label: 'Light UI (WCAG AAA)', description: 'Light mode with AAA contrast', targetBackground: '#ffffff', targetCompliance: 'aaa', weights: { contrast: 0.4, chromaSpacing: 0.2, hueDiversity: 0.2, useCaseSpecific: 0.2 }, specificMetric: lightUIMetric },
    'data-viz': { label: 'Data Visualization', description: 'Charts and graphs', targetBackground: '#ffffff', targetCompliance: 'aa', weights: { contrast: 0.2, chromaSpacing: 0.3, hueDiversity: 0.3, useCaseSpecific: 0.2 }, specificMetric: dataVizMetric },
    'brand-identity': { label: 'Brand Identity', description: 'Brand applications', targetBackground: '#ffffff', targetCompliance: 'aa', weights: { contrast: 0.25, chromaSpacing: 0.25, hueDiversity: 0.3, useCaseSpecific: 0.2 }, specificMetric: brandMetric },
    'print-cmyk': { label: 'Print/CMYK', description: 'CMYK printing', targetBackground: '#ffffff', targetCompliance: 'aa', weights: { contrast: 0.25, chromaSpacing: 0.25, hueDiversity: 0.25, useCaseSpecific: 0.25 }, specificMetric: printMetric },
    'web-accessible': { label: 'Web Accessible', description: 'General web accessibility', targetBackground: '#ffffff', targetCompliance: 'aa', weights: { contrast: 0.3, chromaSpacing: 0.25, hueDiversity: 0.25, useCaseSpecific: 0.2 }, specificMetric: webMetric },
};
// ── Public API: Simple (API/MCP) ────────────────────────────────────────────
/**
 * Simple palette scoring for the API/MCP interface.
 * Modes: 'harmony' | 'accessibility' | 'contrast' | 'comprehensive'
 */
export async function scorePalette(colors, mode = 'comprehensive') {
    if (mode === 'harmony') {
        const result = scoreHarmonyInternal(colors);
        return { score: result.score, details: { pattern: result.pattern, details: result.details }, suggestions: [] };
    }
    if (mode === 'accessibility' || mode === 'contrast') {
        return scoreAccessibility(colors);
    }
    // comprehensive — combine all three
    const harmony = scoreHarmonyInternal(colors);
    const accessibility = await scoreAccessibility(colors);
    const score = Math.round((harmony.score + accessibility.score + accessibility.score) / 3);
    return {
        score,
        details: {
            harmony: harmony.score,
            harmony_pattern: harmony.pattern,
            accessibility: accessibility.details.wcag_aa_pass_rate,
            contrast: accessibility.details.min_contrast_ratio,
        },
        suggestions: accessibility.suggestions,
    };
}
function scoreAccessibility(colors) {
    if (colors.length < 2) {
        return { score: 100, details: { wcag_aa_pass_rate: 1, wcag_aaa_pass_rate: 1, min_contrast_ratio: 21, max_contrast_ratio: 21 }, suggestions: [] };
    }
    let aaPass = 0;
    let aaaPass = 0;
    let totalPairs = 0;
    let minRatio = Infinity;
    let maxRatio = 0;
    const suggestions = [];
    for (let i = 0; i < colors.length; i++) {
        for (let j = i + 1; j < colors.length; j++) {
            totalPairs++;
            try {
                const c1 = chroma(colors[i]);
                const c2 = chroma(colors[j]);
                const l1 = c1.luminance();
                const l2 = c2.luminance();
                const ratio = (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
                minRatio = Math.min(minRatio, ratio);
                maxRatio = Math.max(maxRatio, ratio);
                if (ratio >= 4.5)
                    aaPass++;
                if (ratio >= 7)
                    aaaPass++;
                if (ratio < 3) {
                    suggestions.push({
                        type: 'contrast',
                        message: `Pair ${colors[i]}/${colors[j]} has contrast ratio ${ratio.toFixed(2)}:1 — deficit of ${(4.5 - ratio).toFixed(2)} below AA threshold`,
                    });
                }
            }
            catch {
                totalPairs--;
            }
        }
    }
    const aaRate = totalPairs > 0 ? aaPass / totalPairs : 1;
    const aaaRate = totalPairs > 0 ? aaaPass / totalPairs : 1;
    const score = Math.round(aaRate * 80 + aaaRate * 20);
    return {
        score,
        details: {
            wcag_aa_pass_rate: aaRate,
            wcag_aaa_pass_rate: aaaRate,
            min_contrast_ratio: minRatio === Infinity ? 0 : Number(minRatio.toFixed(2)),
            max_contrast_ratio: Number(maxRatio.toFixed(2)),
            total_pairs: totalPairs,
        },
        suggestions,
    };
}
// ── Public API: Rich (SPA fitness scoring) ──────────────────────────────────
/**
 * Rich use-case-based palette fitness scoring.
 * Returns grade (A-F), metric breakdown, strengths, weaknesses, recommendations.
 */
export function calculatePaletteFitness(colors, useCase) {
    const config = USE_CASE_CONFIGS[useCase];
    const contrastScore = calculateContrastScore(colors, config.targetBackground, config.targetCompliance);
    const chromaScore = calculateChromaSpacingScore(colors);
    const hueScore = calculateHueDiversityScore(colors);
    const specificScore = config.specificMetric(colors);
    const overallScore = contrastScore.score * config.weights.contrast +
        chromaScore.score * config.weights.chromaSpacing +
        hueScore.score * config.weights.hueDiversity +
        specificScore.score * config.weights.useCaseSpecific;
    let grade;
    if (overallScore >= 90)
        grade = 'A';
    else if (overallScore >= 80)
        grade = 'B';
    else if (overallScore >= 70)
        grade = 'C';
    else if (overallScore >= 60)
        grade = 'D';
    else
        grade = 'F';
    const summary = `Palette scores ${overallScore.toFixed(1)}/100 (Grade ${grade}) for ${config.label}`;
    const strengths = [];
    const weaknesses = [];
    const actionableRecommendations = [];
    const allMetrics = [contrastScore, chromaScore, hueScore, specificScore];
    for (const m of allMetrics) {
        if (m.score >= 80)
            strengths.push(`${m.label}: ${m.score.toFixed(0)}/100`);
        else if (m.score < 60)
            weaknesses.push(`${m.label}: ${m.score.toFixed(0)}/100`);
        if (m.score < 80)
            actionableRecommendations.push(...m.recommendations);
    }
    return {
        overallScore,
        grade,
        useCase,
        useCaseLabel: config.label,
        metrics: { contrast: contrastScore, chromaSpacing: chromaScore, hueDiversity: hueScore, useCaseSpecific: specificScore },
        summary,
        strengths,
        weaknesses,
        actionableRecommendations,
    };
}
/**
 * Get the list of available use-case options.
 */
export function getUseCaseOptions() {
    return Object.entries(USE_CASE_CONFIGS).map(([key, config]) => ({
        value: key,
        label: config.label,
        description: config.description,
    }));
}
//# sourceMappingURL=paletteScoring.js.map